import type { AdminPlaylistSummary, ApiError, NeteaseTrackMetadata, Playlist, PlaylistStatus, PlaylistSummary, PreviewResponse } from "@shared/types";

async function request<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const response = await fetch(input, init);
  const payload = (await response.json().catch(() => ({}))) as T & ApiError;
  if (!response.ok) throw new Error(payload.error || "请求失败");
  return payload;
}

export function getPlaylists(): Promise<{ playlists: PlaylistSummary[] }> {
  return request("/api/playlists");
}

export function getPlaylist(slug: string): Promise<{ playlist: Playlist }> {
  return request(`/api/playlists/${encodeURIComponent(slug)}`);
}

export function getNeteaseArtwork(trackId: string, signal?: AbortSignal): Promise<NeteaseTrackMetadata> {
  return request(`/api/netease/tracks/${encodeURIComponent(trackId)}/artwork`, { signal });
}

export function inspectNeteaseTracks(trackIds: string[]): Promise<{ tracks: Record<string, NeteaseTrackMetadata> }> {
  return request("/api/admin/netease/tracks/inspect", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ trackIds }),
  });
}

export function getAdminSession(): Promise<{ authenticated: boolean }> {
  return request("/api/admin/session");
}

export function getAdminPlaylists(): Promise<{ playlists: AdminPlaylistSummary[] }> {
  return request("/api/admin/playlists");
}

export function adminLogin(password: string): Promise<{ authenticated: boolean }> {
  return request("/api/admin/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ password }),
  });
}

export function adminLogout(): Promise<{ authenticated: boolean }> {
  return request("/api/admin/logout", { method: "POST" });
}

export function changeAdminPassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: boolean }> {
  return request("/api/admin/password", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export function previewMarkdown(file: File): Promise<PreviewResponse> {
  const form = new FormData();
  form.append("file", file);
  return request("/api/admin/upload/preview", { method: "POST", body: form });
}

export function publishMarkdown(
  file: File,
  previewToken: string,
): Promise<{ playlist: Playlist }> {
  const form = new FormData();
  form.append("file", file);
  form.append("previewToken", previewToken);
  return request("/api/admin/upload/publish", { method: "POST", body: form });
}

export function setPlaylistStatus(
  slug: string,
  status: PlaylistStatus,
): Promise<{ ok: boolean }> {
  return request(`/api/admin/playlists/${encodeURIComponent(slug)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ status }),
  });
}
